import { Combobox, type ComboboxItem } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversationPersonNames, useConversationsEmployees } from '@/entities/conversations';
import { useComboboxServerSearch } from '@/shared/hooks';
import { getShortName } from '@/shared/lib';
import { RemovableField } from '@/shared/ui';

/** Сколько консультантов предлагать: нужного находят поиском по ФИО, а не листают */
const EMPLOYEE_SUGGESTIONS_LIMIT = 50;

type Props = {
  /** Выбранные консультанты — идентификаторы сотрудников; пусто — отбора по консультанту нет */
  employeeIds: number[];
  onChange: (employeeIds: number[]) => void;
  /** Убрать фильтр с панели */
  onRemove: () => void;
};

/**
 * Фильтр «Консультант» на панели пользовательского дашборда: выбор нескольких консультантов из
 * справочника сотрудников с поиском по ФИО на сервере. Отбираются диалоги любого из выбранных.
 *
 * Фильтр хранит только идентификаторы, поэтому выбранного, которого нет среди найденных, поле
 * показывает по его ФИО, полученному отдельно, — иначе выбор пропал бы из поля при поиске.
 */
export const ConsultantFilterCell: FC<Props> = ({ employeeIds, onChange, onRemove }) => {
  const { t } = useTranslation(['qualityDashboard', 'common']);
  const { search, searchProps } = useComboboxServerSearch();

  const { data, isFetching } = useConversationsEmployees({
    limit: EMPLOYEE_SUGGESTIONS_LIMIT,
    'employee-name': search || undefined,
  });
  const { names } = useConversationPersonNames({ employeeIds, clientIds: [] });

  const options = useMemo<ComboboxItem[]>(() => {
    const foundOptions = (data?.employees ?? []).map((employee) => ({
      value: employee.id,
      label: getShortName(employee.firstName, employee.lastName, employee.middleName),
    }));
    const foundIds = new Set(foundOptions.map((option) => option.value));
    const selectedOptions = employeeIds
      .filter((employeeId) => !foundIds.has(employeeId))
      .map((employeeId) => ({ value: employeeId, label: names[employeeId] ?? String(employeeId) }));
    return [...selectedOptions, ...foundOptions];
  }, [data?.employees, employeeIds, names]);

  return (
    <RemovableField
      label={t('qualityDashboard:filters.consultant')}
      removeLabel={t('common:attributeFilters.removeAttribute')}
      onRemove={onRemove}
    >
      <Combobox
        isMulti
        value={employeeIds}
        onChange={(values: unknown[]) => onChange(values.map(Number))}
        options={options}
        {...searchProps}
        isLoading={isFetching}
        placeholder={t('qualityDashboard:filters.consultantPlaceholder')}
        searchPlaceholder={t('qualityDashboard:filters.consultantSearch')}
        emptyMessage={t('qualityDashboard:filters.consultantsNotFound')}
        className="w-full"
      />
    </RemovableField>
  );
};
